import React, { useState, useEffect, useRef, createContext, useContext } from "react";
import axios from "axios";
import { API } from "../utils/constants";
import { hydrateSourceMap } from "../lib/sources";

const AuthContext = createContext(null);

/** Decode JWT payload without external package */
const decodeTokenPayload = (token) => {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64));
  } catch { return null; }
};

/** Dynamic warning threshold: 24h for long sessions (>7d), 30min for short */
const getWarnBeforeMs = (expMs, iatMs) => {
  const lifetime = expMs - (iatMs || Date.now());
  const sevenDays = 7 * 24 * 60 * 60 * 1000;
  return lifetime > sevenDays ? 24 * 60 * 60 * 1000 : 30 * 60 * 1000;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUserRaw] = useState(null);
  // S-SUNTIME-USERID-NORMALIZATION-V1: Backend liefert user.id — mehrere
  // Workforce-Pages lesen user.user_id. Zentral normalisieren (eine Wahrheit).
  const setUser = (u) => setUserRaw(u ? { ...u, user_id: u.user_id || u.id } : u);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);
  // authReady: true once user + studio context are fully initialized
  const [authReady, setAuthReady] = useState(false);
  
  // Ref to prevent double fetchUser() after login() already handled initialization
  const loginHandledRef = useRef(false);

  // S4.x (2026-04-19): Idempotente Source-Map-Hydration exakt 1x pro User-Session.
  // Verhindert Redundant-Traffic bei Visibility-Resume / Hard-Reload / Token-Restore.
  const sourceMapHydratedRef = useRef(false);
  
  // Token-Expiry-Warning
  const [tokenExpiringSoon, setTokenExpiringSoon] = useState(false);
  const [tokenExpiresAt, setTokenExpiresAt] = useState(null);
  const expiryTimerRef = useRef(null);

  // Studio switching for super admins
  const [activeStudioId, setActiveStudioId] = useState(localStorage.getItem("activeStudioId"));
  const [activeStudio, setActiveStudio] = useState(() => {
    try {
      const stored = localStorage.getItem("activeStudio");
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  // U4-3 FIX: Session-Expired State (Re-Login-Modal statt Hard-Redirect)
  const [sessionExpired, setSessionExpired] = useState(false);
  const sessionExpiredRef = useRef(false);

  // Global 401 interceptor — shows Re-Login-Modal instead of destroying state
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 && token && !sessionExpiredRef.current) {
          sessionExpiredRef.current = true;
          setSessionExpired(true);
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [token]);

  // Token-Expiry-Warning Timer
  useEffect(() => {
    if (expiryTimerRef.current) {
      clearTimeout(expiryTimerRef.current);
      expiryTimerRef.current = null;
    }
    setTokenExpiringSoon(false);
    setTokenExpiresAt(null);

    if (!token) return;

    const payload = decodeTokenPayload(token);
    if (!payload?.exp) return;

    const expMs = payload.exp * 1000;
    const iatMs = payload.iat ? payload.iat * 1000 : null;
    const warnBeforeMs = getWarnBeforeMs(expMs, iatMs);
    const warnAtMs = expMs - warnBeforeMs;

    setTokenExpiresAt(new Date(expMs));

    // setTimeout max safe value: 2^31-1 (~24.8 days). Longer delays overflow to 0.
    // Recursive scheduling: cap at safe max, re-evaluate on fire.
    const MAX_SAFE_TIMEOUT = 2147483647;
    const scheduleCheck = () => {
      const remaining = warnAtMs - Date.now();
      if (remaining <= 0) {
        setTokenExpiringSoon(true);
        return;
      }
      expiryTimerRef.current = setTimeout(scheduleCheck, Math.min(remaining, MAX_SAFE_TIMEOUT));
    };
    scheduleCheck();

    return () => {
      if (expiryTimerRef.current) {
        clearTimeout(expiryTimerRef.current);
        expiryTimerRef.current = null;
      }
    };
  }, [token]);

  // On mount / token change: only fetchUser if login() didn't already handle it
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      if (loginHandledRef.current) {
        // login() already initialized everything — skip redundant fetchUser
        loginHandledRef.current = false;
      } else {
        // Page refresh or initial load — need to hydrate from server
        fetchUser();
      }
    } else {
      setLoading(false);
      setAuthReady(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchUser pro-Render neu; als dep => Auth-Refetch-Loop. Reagiert bewusst nur auf token (ARR-HOOKS-E)
  }, [token]);

  // PWA Visibility-Change: Bei App-Resume User-Daten auffrischen (mobile lifecycle)
  useEffect(() => {
    if (!token) return;
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && token && user) {
        // App kam zurück in den Vordergrund — sanft User-Daten auffrischen
        // fetchUser() hat jetzt sichere Fehlerbehandlung (kein Logout bei Netzwerkfehler)
        fetchUser();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchUser pro-Render neu; Resume-Refresh reagiert bewusst nur auf token/user-Präsenz
  }, [token, user]);

  // Update active studio persistence when it changes.
  // CQ-P4-005: X-Studio-Id war ein toter Kanal (0 Backend-Reader) — Sender entfernt.
  // localStorage bleibt UI-State-Quelle; Tenant-Wahrheit läuft server-seitig über JWT.
  useEffect(() => {
    if (activeStudioId) {
      localStorage.setItem("activeStudioId", activeStudioId);
    } else {
      localStorage.removeItem("activeStudioId");
    }
  }, [activeStudioId]);

  // S4.x (2026-04-19): Source-Map aus Backend hydrieren — idempotent pro Session.
  // Single Source of Truth = backend/app/core/source_map.py. Feuert genau einmal,
  // sobald user zum ersten Mal gesetzt ist. Fire-and-forget — bei Netzwerkfehler
  // bleibt die gebundelte Default-Map aktiv. Reset auf Logout via Cleanup.
  useEffect(() => {
    if (user && !sourceMapHydratedRef.current) {
      sourceMapHydratedRef.current = true;
      axios.get(`${API}/statistics/source-map`)
        .then(r => hydrateSourceMap(r.data))
        .catch(() => { /* Default-Fallback bleibt aktiv */ });
    }
    if (!user) {
      // Logout / Token-Expiry: Flag zuruecksetzen, damit naechster Login wieder hydriert.
      sourceMapHydratedRef.current = false;
    }
  }, [user]);

  const fetchUser = async () => {
    try {
      // FIX 1: Parallelize auth/me + my-studio-logo (both need only the auth token)
      const [authRes, studioLogoRes] = await Promise.all([
        axios.get(`${API}/auth/me`),
        axios.get(`${API}/my-studio-logo`).catch(() => null)
      ]);
      const userData = authRes.data;

      // Resolve studio data using parallel results
      let studioId = null;
      let studioData = null;

      if (userData.role === "super_admin") {
        // Super admins use a different studio source — ignore studioLogoRes
        if (activeStudioId) {
          try {
            const studioRes = await axios.get(`${API}/studios/${activeStudioId}`);
            studioId = activeStudioId;
            studioData = studioRes.data;
          } catch {
            localStorage.removeItem("activeStudioId");
            localStorage.removeItem("activeStudio");
          }
        }
        // If still no active studio, auto-select the first one
        if (!studioId) {
          try {
            const studiosRes = await axios.get(`${API}/studios`);
            if (studiosRes.data && studiosRes.data.length > 0) {
              const firstStudio = studiosRes.data[0];
              studioId = firstStudio.id;
              studioData = firstStudio;
              localStorage.setItem("activeStudioId", firstStudio.id);
              localStorage.setItem("activeStudio", JSON.stringify(firstStudio));
            }
          } catch (e) {
            console.error("Error fetching studios:", e);
          }
        }
      } else if (userData.studio_id) {
        // Admin/Employee: use the parallel studioLogoRes
        if (studioLogoRes?.data) {
          studioData = { id: userData.studio_id, name: studioLogoRes.data?.name || "Suntime No.1", logo_url: studioLogoRes.data?.logo_url || null };
        } else {
          studioData = { id: userData.studio_id, name: "Suntime No.1" };
        }
      }

      // BATCH all state updates — single React render
      setUser(userData);
      if (studioData) {
        if (userData.role === "super_admin" && studioId) {
          setActiveStudioId(studioId);
        }
        setActiveStudio(studioData);
      } else if (userData.role === "super_admin") {
        setActiveStudioId(null);
        setActiveStudio(null);
      }
    } catch (e) {
      // Don't logout on aborted/cancelled requests (e.g. during navigation)
      if (e?.code === 'ERR_CANCELED' || e?.message === 'canceled') {
        // Navigation cancelled — ignore silently
      } else if (e.response?.status === 401) {
        // U4-3: 401 → Interceptor zeigt Re-Login-Modal (kein logout hier)
      } else if (e.response?.status === 403) {
        // Forbidden — Token ist ungültig, sauber abmelden
        logout();
      } else {
        // Netzwerkfehler, Timeout, Server nicht erreichbar:
        // Token in localStorage ist noch gültig → Session NICHT zerstören.
        // Beim nächsten Seitenaufruf/Visibility-Change wird erneut versucht.
        console.warn("[SUNTIME] AUTH_FETCH_FAILED (session preserved)", {
          code: e?.code,
          message: e?.message,
          hasResponse: !!e?.response,
        });
        // Wenn wir bereits einen User im State haben, behalten wir ihn
        // Nur wenn es der erste Load ist und kein User existiert, bleiben wir im loading
        if (!user) {
          // Erster Load fehlgeschlagen — versuche den Token-Payload als Fallback
          const storedToken = localStorage.getItem("token");
          if (storedToken) {
            const payload = decodeTokenPayload(storedToken);
            if (payload && payload.exp * 1000 > Date.now()) {
              // Token ist noch nicht abgelaufen → minimal User-State setzen
              // FIX (2026-04-19): studio_id aus persistiertem Kontext hydratisieren,
              // damit nachfolgende Create-Requests (ErfassungPage etc.) den Studio-Kontext
              // mitsenden koennen. Ohne diese Hydration schlug Contract-POST mit
              // "studio_id: Field required" fehl (Mobile-PWA-Resume-Szenario).
              let fallbackStudioId = activeStudioId || null;
              if (!fallbackStudioId) {
                try {
                  const stored = JSON.parse(localStorage.getItem("activeStudio") || "null");
                  if (stored?.id) fallbackStudioId = stored.id;
                } catch { /* ignore parse errors */ }
              }
              setUser({ 
                username: payload.sub || payload.username,
                role: payload.role || "employee",
                name: payload.name || payload.sub || "User",
                studio_id: fallbackStudioId
              });
            } else {
              // Token abgelaufen → abmelden
              logout();
            }
          }
        }
      }
    } finally {
      setLoading(false);
      setAuthReady(true);
    }
  };

  const login = async (username, password, totpCode, rememberMe = false) => {
    const payload = { username, password, remember_me: rememberMe };
    if (totpCode) payload.totp_code = totpCode;
    
    const res = await axios.post(`${API}/auth/login`, payload);
    
    // Check if 2FA is required
    if (res.data.requires_2fa) {
      return { requires_2fa: true };
    }
    
    const { token: newToken, user: userData } = res.data;
    
    // CRITICAL: Set ref BEFORE setToken to prevent useEffect[token] from calling fetchUser
    loginHandledRef.current = true;
    
    localStorage.setItem("token", newToken);
    axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
    
    // Complete ALL studio initialization BEFORE setting React state
    // (CQ-P4-005: kein X-Studio-Id-Header mehr; Studio-Kontext via localStorage/State)
    let studioId = null;
    let studioData = null;
    
    if (userData.role === "super_admin") {
      const storedStudioId = localStorage.getItem("activeStudioId");
      if (storedStudioId) {
        try {
          const studioRes = await axios.get(`${API}/studios/${storedStudioId}`);
          studioId = storedStudioId;
          studioData = studioRes.data;
        } catch {
          localStorage.removeItem("activeStudioId");
          localStorage.removeItem("activeStudio");
        }
      }
      if (!studioId) {
        try {
          const studiosRes = await axios.get(`${API}/studios`);
          if (studiosRes.data?.length > 0) {
            const firstStudio = studiosRes.data[0];
            studioId = firstStudio.id;
            studioData = firstStudio;
          }
        } catch (e) {
          console.error("Error fetching studios:", e);
        }
      }
    } else if (userData.studio_id) {
      studioId = userData.studio_id;
      try {
        const studioRes = await axios.get(`${API}/my-studio-logo`);
        studioData = { id: userData.studio_id, name: studioRes.data?.name || "Suntime No.1", logo_url: studioRes.data?.logo_url || null };
      } catch {
        studioData = { id: userData.studio_id, name: "Suntime No.1" };
      }
    }
    
    // Persist studio context synchronously before React state updates
    if (studioId) {
      localStorage.setItem("activeStudioId", studioId);
      if (studioData) {
        localStorage.setItem("activeStudio", JSON.stringify(studioData));
      }
    }
    
    // UX-2 §19 (Modal-Defusing): Popup-Datumsmarker werden beim Login NICHT
    // mehr geloescht — Motivation/Upgrade/Weekly-Champion erscheinen dadurch
    // max. 1x pro Tag/Woche statt bei jedem Login. Logo-Klick zeigt die
    // Motivation weiterhin bewusst erneut (DashboardPage-Event).
    
    // S-AUTH-DOUBLE-LOGIN-FIX-V1 (2026-08-05, per V3-Forensik freigegeben):
    // Latenter sessionExpired-Zustand aus Cold-Start-401 wird beim regulaeren
    // Login getilgt — sonst erscheint das "Sitzung abgelaufen"-Modal nach dem
    // ersten erfolgreichen Login (Doppel-Login-Bug).
    sessionExpiredRef.current = false;
    setSessionExpired(false);

    // Now batch all React state updates together
    setToken(newToken);
    setUser(userData);
    setActiveStudioId(studioId);
    setActiveStudio(studioData);
    setLoading(false);
    setAuthReady(true);

    return userData;
  };

  const logout = () => {
    if (expiryTimerRef.current) {
      clearTimeout(expiryTimerRef.current);
      expiryTimerRef.current = null;
    }
    setTokenExpiringSoon(false);
    setTokenExpiresAt(null);
    localStorage.removeItem("token");
    localStorage.removeItem("activeStudioId");
    localStorage.removeItem("activeStudio");
    delete axios.defaults.headers.common["Authorization"];
    setToken(null);
    setUser(null);
    setActiveStudioId(null);
    setActiveStudio(null);
    setAuthReady(false);
  };

  // ── U4-3: Re-Login (statt Hard-Redirect bei 401) ──
  const reLogin = async (username, password) => {
    // Saubere Axios-Instanz ohne Interceptor / expired Authorization-Header
    const clean = axios.create();
    const res = await clean.post(`${API}/auth/login`, { username, password });
    const { token: newToken, user: userData } = res.data;

    // Token ersetzen
    localStorage.setItem("token", newToken);
    axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
    loginHandledRef.current = true;
    setToken(newToken);

    // User-State auffrischen
    const studioId = userData.studio_id || activeStudioId;
    setUser({ ...userData, studio_id: studioId });

    // Expiry-Timer neu aufsetzen
    const payload = decodeTokenPayload(newToken);
    if (payload?.exp) {
      const expMs = payload.exp * 1000;
      setTokenExpiresAt(new Date(expMs));
      const warn = getWarnBeforeMs(expMs, payload.iat ? payload.iat * 1000 : Date.now());
      const diff = expMs - Date.now() - warn;
      if (expiryTimerRef.current) clearTimeout(expiryTimerRef.current);
      setTokenExpiringSoon(false);
      if (diff > 0) {
        expiryTimerRef.current = setTimeout(() => setTokenExpiringSoon(true), diff);
      }
    }

    // Session als wiederhergestellt markieren
    sessionExpiredRef.current = false;
    setSessionExpired(false);
    setAuthReady(true);
  };

  const dismissSessionExpired = () => {
    // Fallback: User möchte komplett zur Login-Seite
    sessionExpiredRef.current = false;
    setSessionExpired(false);
    logout();
  };

  const switchStudio = async (studioId, studioData = null) => {
    // OP2-D: auch Operator-User duerfen wechseln — Autorisierung entscheidet der Server
    try {
      // Call backend to get a new token with the switched studio
      const res = await axios.post(`${API}/auth/switch-studio`, { studio_id: studioId });
      const { token: newToken, studio } = res.data;
      
      // Update token in storage and axios header - do this synchronously before redirect
      const studioToSave = studio || studioData;
      localStorage.setItem("token", newToken);
      localStorage.setItem("activeStudioId", studioId);
      localStorage.setItem("activeStudio", JSON.stringify(studioToSave));
      
      // Small delay to ensure localStorage is written
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Reload the page to refresh all data with new studio context
      window.location.href = "/dashboard";
    } catch (error) {
      console.error("Error switching studio:", error);
      // Fallback: just set local state without new token
      setActiveStudioId(studioId);
      if (studioData) {
        setActiveStudio(studioData);
      } else if (studioId) {
        try {
          const studioRes = await axios.get(`${API}/studios/${studioId}`);
          setActiveStudio(studioRes.data);
        } catch {
          setActiveStudio(null);
        }
      }
    }
  };

  const resetToOwnStudio = async () => {
    try {
      // OP2-D (ARR-SAAS-OP2-028): Server erzeugt frischen Token OHNE stale Claim
      const res = await axios.post(`${API}/auth/reset-home-studio`);
      localStorage.setItem("token", res.data.token);
      axios.defaults.headers.common["Authorization"] = `Bearer ${res.data.token}`;
    } catch (error) {
      console.error("Error resetting studio token:", error);
    }
    localStorage.removeItem("activeStudioId");
    localStorage.removeItem("activeStudio");
    setActiveStudioId(null);
    setActiveStudio(null);
    // Full-Reload invalidiert saemtliche studio-gebundenen Frontend-States
    window.location.href = "/dashboard";
  };

  // Effective studio ID (for super admins, use active studio; otherwise use user's studio)
  const effectiveStudioId = user?.role === "super_admin" && activeStudioId 
    ? activeStudioId 
    : user?.studio_id;

  const value = {
    user,
    token,
    login,
    logout,
    loading,
    authReady,
    isAdmin: user?.role === "admin" || user?.role === "super_admin",
    isSuperAdmin: user?.role === "super_admin",
    // S-TABLET-RBAC-CUSTOMERS-CONTRACTS-V1: Tablet-Indikator + Permission-Check.
    // `permissions` kommt aus /api/auth/me und ist die SSOT fuer Sidebar +
    // Route-Guards. Frontend-Hiding alleine reicht nicht — Backend setzt 403.
    isTablet: user?.role === "tablet",
    hasPermission: (perm) => Array.isArray(user?.permissions) && user.permissions.includes(perm),
    isAuthenticated: !!user,
    // Studio switching
    activeStudioId,
    activeStudio,
    effectiveStudioId,
    switchStudio,
    resetToOwnStudio,
    // Show banner when super admin has actively switched to a studio
    isViewingOtherStudio: user?.role === "super_admin" && !!activeStudioId && !!activeStudio,
    // Token-Expiry-Warning
    tokenExpiringSoon,
    tokenExpiresAt,
    // U4-3: Re-Login-Modal
    sessionExpired,
    reLogin,
    dismissSessionExpired
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
